import { noop } from 'lodash-es'

import { CostMessagingCoverageModalData } from '../../common/constants/constants'
import { EssentialsContentSection, EssentialsUpcomingEventData } from '../../essentials/types'
import { NeurodiversitySearcherType } from '../types'

export interface GetNeurodiversityToolkitPagePropsParams {
  isUserInternational: boolean
  isUserTeen: boolean
  hideChildSearch: boolean
  isASDCoordinationEnabled: boolean
  isNeuropsychologicalEvalEnabled: boolean
  isCaregiverTrainingEnabled: boolean
  onPressExploreCareOptions: (searcherType: NeurodiversitySearcherType) => void
  onPressGetInTouch?: () => void
  userDisplayLanguage?: string
  eventsList?: EssentialsUpcomingEventData[]
  eventsLoading?: boolean
  onPressUpcomingEssentialsEventsHeaderLink?: () => void
  onPressUpcomingEssentialsEventsCard?: (eventSlug: string) => void
  isMedsSupported: boolean
  costMessagingInfo: CostMessagingCoverageModalData
  trackCoverageModalPress: (isOpen: boolean) => void
  essentialsContents?: EssentialsContentSection[]
  onPressEssentialsSeeMore?: () => void
  isEssentialsEnabled?: boolean
  onFAQQuestionPress?: (title: string) => void
  legalNotice?: React.ReactNode
  onFooterLinkPress: (url: string) => void
}

export interface NeurodiversityToolkitPageProps {
  commonSectionProps: {
    isUserInternational: boolean
    isUserTeen: boolean
    hideChildSearch: boolean
    isASDCoordinationEnabled: boolean
    isNeuropsychologicalEvalEnabled: boolean
    isCaregiverTrainingEnabled: boolean
    onPressExploreCareOptions: (searcherType: NeurodiversitySearcherType) => void
    onPressGetInTouch: () => void
  }
  essentialsSectionProps: {
    userDisplayLanguage?: string
    upcomingEvents?: EssentialsUpcomingEventData[]
    eventsLoading?: boolean
    onPressUpcomingEssentialsEventsHeaderLink?: () => void
    onPressUpcomingEssentialsEventsCard?: (eventSlug: string) => void
    essentialsSections?: EssentialsContentSection[]
    onSeeMorePress?: () => void
    isEssentialsEnabled?: boolean
  }
  faqSectionProps: {
    isMedsSupported: boolean
    costMessagingProps: CostMessagingCoverageModalData & {
      trackCoverageModalPress: (isOpen: boolean) => void
    }
    onQuestionPress: (title: string) => void
  }
  legalSectionProps: {
    legalNotice?: React.ReactNode
    footerLinkPressed: (url: string) => void
    isUserInternational: boolean
  }
}

/**
 * Cross-platform hook that provides props for NeurodiversityToolkitPage
 * Consolidates shared logic between mobile and web implementations
 *
 * @param params Platform-specific dependencies and handlers
 * @returns Props object for NeurodiversityToolkitPage component
 */
export const getNeurodiversityToolkitPageProps = ({
  isUserInternational,
  isUserTeen,
  hideChildSearch,
  isASDCoordinationEnabled,
  isNeuropsychologicalEvalEnabled,
  isCaregiverTrainingEnabled,
  onPressExploreCareOptions = noop,
  onPressGetInTouch = noop,
  userDisplayLanguage,
  eventsList,
  eventsLoading,
  onPressUpcomingEssentialsEventsHeaderLink = noop,
  onPressUpcomingEssentialsEventsCard = noop,
  isMedsSupported,
  costMessagingInfo,
  trackCoverageModalPress,
  essentialsContents,
  onPressEssentialsSeeMore = noop,
  isEssentialsEnabled,
  onFAQQuestionPress = noop,
  legalNotice,
  onFooterLinkPress = noop,
}: GetNeurodiversityToolkitPagePropsParams): NeurodiversityToolkitPageProps => {
  return {
    commonSectionProps: {
      isUserInternational,
      isUserTeen,
      hideChildSearch,
      isASDCoordinationEnabled,
      isNeuropsychologicalEvalEnabled,
      isCaregiverTrainingEnabled,
      onPressExploreCareOptions,
      onPressGetInTouch,
    },
    essentialsSectionProps: {
      userDisplayLanguage,
      upcomingEvents: eventsList,
      eventsLoading,
      onPressUpcomingEssentialsEventsHeaderLink,
      onPressUpcomingEssentialsEventsCard,
      essentialsSections: essentialsContents,
      onSeeMorePress: onPressEssentialsSeeMore,
      isEssentialsEnabled,
    },
    faqSectionProps: {
      isMedsSupported,
      costMessagingProps: {
        ...costMessagingInfo,
        trackCoverageModalPress,
      },
      onQuestionPress: onFAQQuestionPress,
    },
    legalSectionProps: {
      legalNotice,
      footerLinkPressed: onFooterLinkPress,
      isUserInternational,
    },
  }
}
